import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  TableSortLabel,
  Switch,
  DialogContentText,
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { supabase } from '../../lib/supabase';
import { 
  RefreshIcon, 
  SearchIcon,
  AutorenewIcon,
  EditIcon,
} from '../../components/icons';
import { getSimBalance } from '../../services/megsappService';
import { useAuth } from '../../contexts/AuthContext';
import { BulkSimAssignModal } from '../../components/BulkSimAssignModal';
import { Rule } from '../../components/AutoTopUpConfigModal';
import { AutoTopUpConfigModal } from '../../components/AutoTopUpConfigModal';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { ReassignSimModal } from '../../components/ReassignSimModal';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { visuallyHidden } from '@mui/utils';
import { AssignSimModal } from '../../components/AssignSimModal';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import UsageLimitOverrideModal from '../../components/UsageLimitOverrideModal';

type Cluster = {
  id: string;
  name: string;
  is_critical: boolean;
};

export type SimCard = {
  id: string;
  organization_id: string;
  organization: {
    name: string;
    low_balance_threshold?: number;
  };
  msisdn: string | null;
  iccid: string | null;
  status: string;
  auto_topup_enabled: boolean;
  auto_topup_config: Rule[] | null; // Use updated Rule[]
  balances: any;
  balance_last_checked: string | null;
  created_at: string;
  updated_at: string;
  cluster_id: string | null;
  clusters: {
    id?: string;
    name?: string;
    is_critical?: boolean;
    auto_topup_enabled?: boolean;
    auto_topup_config?: Rule[] | null;
    default_sim_status?: string;
    usage_limit_value?: number | null;
  } | null;
  is_critical_override: boolean | null;
  status_override: boolean;
  usage_limit_override_value: number | null;
  current_period_topup_spend: number;
  current_period_start: string;
};

function getEffectiveCriticality(sim: SimCard): boolean {
  if (sim.is_critical_override !== null) {
    return sim.is_critical_override;
  }
  return sim.clusters?.is_critical ?? false;
}

function getCriticalitySource(sim: SimCard): string {
  if (sim.is_critical_override !== null) {
    return sim.is_critical_override ? 'Override: Yes' : 'Override: No';
  }
  return sim.clusters?.is_critical ? 'Inherited: Yes' : 'Inherited: No';
}

// Helper function for stable sorting
function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number): T[] {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

// Helper function to get descending comparator
function getComparator<Key extends keyof any>(
  order: 'asc' | 'desc',
  orderBy: Key,
): (a: { [key in Key]: any }, b: { [key in Key]: any }) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// Helper function for comparing values (refactored for type safety)
function descendingComparator<T>(a: T, b: T, orderBy: keyof T): number {
  let compA: string | number | null | undefined;
  let compB: string | number | null | undefined;

  // Extract values for comparison based on orderBy key
  if (orderBy === 'clusters') {
    compA = (a as SimCard).clusters?.name?.toLowerCase() || '';
    compB = (b as SimCard).clusters?.name?.toLowerCase() || '';
  } else if (orderBy === 'organization') {
    compA = (a as SimCard).organization?.name?.toLowerCase() || '';
    compB = (b as SimCard).organization?.name?.toLowerCase() || '';
  } else if (orderBy === 'balance_last_checked') {
    // Treat null/Never as earliest
    const dateA = a[orderBy] as string | null;
    const dateB = b[orderBy] as string | null;
    compA = dateA ? new Date(dateA).getTime() : 0;
    compB = dateB ? new Date(dateB).getTime() : 0;
  } else {
    // Default comparison for other fields (assuming primitive types)
    compA = a[orderBy] as string | number | null | undefined;
    compB = b[orderBy] as string | number | null | undefined;
     // Optional: Add lowercase for string comparison here if needed generally
     if (typeof compA === 'string' && typeof compB === 'string') {
       compA = compA.toLowerCase();
       compB = compB.toLowerCase();
     }
     // Handle potential null/undefined for numeric comparison
     if (typeof compA === 'number' || compA === null || compA === undefined) {
       compA = compA ?? 0;
     }
      if (typeof compB === 'number' || compB === null || compB === undefined) {
       compB = compB ?? 0;
     }
  }

  // Perform comparison on the extracted values
  if (compB === undefined || compB === null || compB < compA) {
    return -1;
  }
  if (compA === undefined || compA === null || compB > compA) {
    return 1;
  }
  return 0;
}

// Define which columns are sortable
interface HeadCell {
  id: keyof SimCard | 'clusters' | 'organization' | 'balance_last_checked' | 'status' | 'status_override'; // Adjust to include nested keys if needed
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  { id: 'msisdn', numeric: false, label: 'MSISDN' },
  { id: 'iccid', numeric: false, label: 'ICCID' },
  { id: 'clusters', numeric: false, label: 'Cluster' },
  { id: 'is_critical_override', numeric: false, label: 'Critical' },
  { id: 'organization', numeric: false, label: 'Organization' },
  { id: 'status', numeric: false, label: 'Effective Status' },
];

export function SimManagement() {
  const [sims, setSims] = useState<SimCard[]>([]);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [selectedClusterFilter, setSelectedClusterFilter] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshingSim, setRefreshingSim] = useState<string | null>(null);
  const [selectedSim, setSelectedSim] = useState<SimCard | null>(null);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [criticalityModalOpen, setCriticalityModalOpen] = useState(false);
  const [selectedSimForCrit, setSelectedSimForCrit] = useState<SimCard | null>(null);
  const [bulkAssignModalOpen, setBulkAssignModalOpen] = useState(false);
  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const [simToReassign, setSimToReassign] = useState<SimCard | null>(null);
  const [assignSimModalOpen, setAssignSimModalOpen] = useState(false);
  const [usageLimitModalOpen, setUsageLimitModalOpen] = useState(false);
  const [selectedSimForLimit, setSelectedSimForLimit] = useState<SimCard | null>(null);
  const { profile } = useAuth();
  const organizationId = profile?.organization_id;
  const userRole = profile?.role;
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<keyof SimCard | 'clusters' | 'organization' | 'balance_last_checked' | 'status' | 'status_override'>('msisdn');
  const [updatingStatusSimId, setUpdatingStatusSimId] = useState<string | null>(null);
  const [confirmDeactivateOpen, setConfirmDeactivateOpen] = useState(false);
  const [simToDeactivate, setSimToDeactivate] = useState<SimCard | null>(null);

  const fetchClustersForFilter = useCallback(async () => {
    if (!organizationId) return;
    try {
      const { data, error: clusterError } = await supabase
        .from('clusters')
        .select('id, name, is_critical')
        .eq('organization_id', organizationId)
        .order('name');

      if (clusterError) throw clusterError;
      setClusters(data || []);
    } catch (err: any) {
      console.error("Error fetching clusters for filter:", err);
    }
  }, [organizationId]);

  const fetchSims = useCallback(async () => {
    if (!organizationId) {
        setLoading(false);
        setError("Cannot load SIMs: User organization not found.");
        setSims([]);
        return; 
    }
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('sim_cards')
        .select(`
          *,
          status_override, 
          organization:organizations!inner(name, low_balance_threshold),
          clusters( 
            id, name, is_critical, 
            auto_topup_enabled, auto_topup_config,
            default_sim_status, usage_limit_value
          )
        `)
        .eq('organization_id', organizationId)
        .ilike('msisdn', `%${searchQuery}%`);

      if (selectedClusterFilter) {
        query = query.eq('cluster_id', selectedClusterFilter);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;
      setSims(data as SimCard[] || []); 
    } catch (err: any) {
      console.error('Error fetching SIMs:', err);
      setError(err.message);
      setSims([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId, searchQuery, selectedClusterFilter]);

  useEffect(() => {
    if (organizationId) {
        fetchSims();
        fetchClustersForFilter();
    } else {
        setLoading(false);
        setSims([]);
        setClusters([]);
    }
  }, [organizationId, fetchSims, fetchClustersForFilter]);

  const handleRefreshBalance = useCallback(async (msisdn: string) => {
    try {
      setRefreshingSim(msisdn);
      await getSimBalance(msisdn);
      fetchSims();
    } catch (err: any) {
      console.error('Error refreshing balance:', err);
      setError(`Failed to refresh balance for ${msisdn}: ${err.message}`);
    } finally {
      setRefreshingSim(null);
    }
  }, [fetchSims]);

  const handleConfigureAutoTopUp = useCallback((sim: SimCard) => {
    console.log('Opening config modal for SIM:', sim.id);
    setSelectedSim(sim);
    setConfigModalOpen(true);
  }, []);

  const handleConfigModalClose = async (
    updatedConfig?: { enabled: boolean; rules: Rule[] } | null 
  ) => {
    setConfigModalOpen(false);
    if (updatedConfig && selectedSim) {
      console.log('[SimManagement] ConfigModal closed with update:', updatedConfig);
      
      // Save to database
      try {
        const { error } = await supabase
          .from('sim_cards')
          .update({
            auto_topup_enabled: updatedConfig.enabled,
            auto_topup_config: updatedConfig.enabled ? updatedConfig.rules : null,
            updated_at: new Date().toISOString()
          })
          .eq('id', selectedSim.id);

        if (error) {
          console.error('Error saving auto top-up config:', error);
          setError(`Failed to save auto top-up configuration: ${error.message}`);
          setSelectedSim(null);
          return;
        }

        console.log('Auto top-up config saved successfully for SIM:', selectedSim.id);
        
        // Update local UI state only after successful database save
        setSims(prevSims =>
          prevSims.map(sim =>
            sim.id === selectedSim.id
              ? { 
                  ...sim, 
                  auto_topup_enabled: updatedConfig.enabled,
                  auto_topup_config: updatedConfig.rules 
                }
              : sim
          )
        );
      } catch (err: any) {
        console.error('Error saving auto top-up config:', err);
        setError(`Failed to save auto top-up configuration: ${err.message}`);
      }
    }
    setSelectedSim(null);
  };

  const handleOpenCriticalityModal = useCallback((sim: SimCard) => {
    setSelectedSimForCrit(sim);
    setCriticalityModalOpen(true);
  }, []);

  const handleCloseCriticalityModal = useCallback((refresh = false) => {
    setSelectedSimForCrit(null);
    setCriticalityModalOpen(false);
    if (refresh) {
      fetchSims();
    }
  }, [fetchSims]);

  const handleOpenBulkAssignModal = () => {
    setBulkAssignModalOpen(true);
  };

  const handleCloseBulkAssignModal = () => {
    setBulkAssignModalOpen(false);
  };

  const handleOpenReassignModal = (sim: SimCard) => {
    setSimToReassign(sim);
    setReassignModalOpen(true);
  };

  const handleCloseReassignModal = (refresh: boolean = false) => {
    setSimToReassign(null);
    setReassignModalOpen(false);
    if (refresh) {
      fetchSims();
    }
  };

  const handleOpenAssignSimModal = () => {
    setAssignSimModalOpen(true);
  };

  const handleCloseAssignSimModal = (refresh: boolean = false) => {
    setAssignSimModalOpen(false);
    if (refresh) {
      fetchSims();
    }
  };

  const handleOpenUsageLimitModal = useCallback((sim: SimCard) => {
    setSelectedSimForLimit(sim);
    setUsageLimitModalOpen(true);
  }, []);

  const handleCloseUsageLimitModal = useCallback((refresh = false) => {
    setSelectedSimForLimit(null);
    setUsageLimitModalOpen(false);
    if (refresh) {
      fetchSims();
    }
  }, [fetchSims]);

  const formatDataBalance = (mb: number) => {
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(2)} GB`;
    }
    return `${mb.toFixed(2)} MB`;
  };

  // Handler for sort requests
  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof SimCard | 'clusters' | 'organization' | 'balance_last_checked' | 'status' | 'status_override',
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Create sort handler function for TableSortLabel
   const createSortHandler = (
    property: keyof SimCard | 'clusters' | 'organization' | 'balance_last_checked' | 'status' | 'status_override',
  ) => (event: React.MouseEvent<unknown>) => {
    handleRequestSort(event, property);
  };

  // Memoize sorted SIMs
  const sortedSims = useMemo(() => {
    return stableSort(sims, getComparator(order, orderBy));
  }, [sims, order, orderBy]);

  // --- Handler for toggling individual SIM status --- 
  const handleSimStatusToggle = useCallback(async (sim: SimCard, confirmed: boolean = false) => {
    const effectiveCurrentStatus = getEffectiveSimStatus(sim);
    const newStatus = effectiveCurrentStatus === 'active' ? 'inactive' : 'active';

    // If moving to inactive AND not yet confirmed, open dialog
    if (newStatus === 'inactive' && !confirmed) {
      setSimToDeactivate(sim);
      setConfirmDeactivateOpen(true);
      return; 
    }

    // Proceed with update (either activating or confirmed deactivation)
    setConfirmDeactivateOpen(false); // Close dialog if it was open
    setUpdatingStatusSimId(sim.id);
    setError(null);
    console.log(`Toggling SIM ${sim.id} status to ${newStatus}`);

    try {
      const { data: rpcData, error: rpcError } = await supabase.rpc('update_sim_status', {
        target_sim_id: sim.id,
        new_status: newStatus
      });

      if (rpcError) throw rpcError;
      if (!rpcData?.success) throw new Error(rpcData?.error || 'Failed to update SIM status via RPC.');

      // Update local state
      setSims(prevSims => prevSims.map(s => 
          s.id === sim.id ? { ...s, status: newStatus, status_override: true } : s
      ));

    } catch (err: any) {
      console.error("Error updating SIM status:", err);
      setError(err.message || `Failed to update status for SIM ${sim.id}.`);
    } finally {
      setUpdatingStatusSimId(null);
      setSimToDeactivate(null); // Clear sim from dialog state
    }
  }, []); 

  // --- Helper to get effective status --- 
  function getEffectiveSimStatus(sim: SimCard): 'active' | 'inactive' {
    if (sim.status_override) {
      return sim.status as 'active' | 'inactive'; // Use its own status if overridden
    }
    // Otherwise, use the cluster default, defaulting to 'active' if cluster/default is missing
    return (sim.clusters?.default_sim_status as 'active' | 'inactive') ?? 'active';
  }

  // Add helper to get effective usage limit
  function getEffectiveUsageLimit(sim: SimCard): number | null {
    if (sim.usage_limit_override_value !== null) {
      return sim.usage_limit_override_value;
    }
    return sim.clusters?.usage_limit_value ?? null;
  }

  // Add helper to get usage limit source
  function getUsageLimitSource(sim: SimCard): string {
    if (sim.usage_limit_override_value !== null) {
      return 'Override: ' + (sim.usage_limit_override_value === 0 
        ? 'R0 (blocked)' 
        : sim.usage_limit_override_value === null 
          ? 'No limit' 
          : `R${sim.usage_limit_override_value}`);
    }
    return 'Inherited: ' + (sim.clusters?.usage_limit_value === null 
      ? 'No limit' 
      : `R${sim.clusters?.usage_limit_value}`);
  }

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          SIM Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            select
            size="small"
            label="Filter by Cluster"
            value={selectedClusterFilter}
            onChange={(e) => setSelectedClusterFilter(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">
              <em>All Clusters</em>
            </MenuItem>
            {clusters.map((cluster) => (
              <MenuItem key={cluster.id} value={cluster.id}>
                {cluster.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            placeholder="Search by MSISDN..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && fetchSims()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          {userRole === 'superadmin' && (
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={handleOpenBulkAssignModal}
              disabled={loading}
            >
              Bulk Assign SIMs
            </Button>
          )}
          {userRole === 'superadmin' && (
            <Button
              variant="contained"
              onClick={handleOpenAssignSimModal}
              disabled={loading}
            >
              Assign New SIM
            </Button>
          )}
        </Box>
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {error && (
          <Box sx={{ p: 2 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {headCells.map((headCell) => (
                    <TableCell
                      key={headCell.id}
                      align={headCell.numeric ? 'right' : 'left'}
                      sortDirection={orderBy === headCell.id ? order : false}
                      sx={{
                        ...(headCell.id === 'msisdn' && { width: '10%' }),
                        ...(headCell.id === 'iccid' && { width: '15%' }),
                        ...(headCell.id === 'clusters' && { width: '10%' }),
                        ...(headCell.id === 'is_critical_override' && { width: '7%', textAlign: 'center' }),
                        ...(headCell.id === 'organization' && { width: '10%' }),
                        ...(headCell.id === 'status' && { width: '12%' }),
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : 'asc'}
                        onClick={createSortHandler(headCell.id)}
                      >
                        {headCell.label}
                        {orderBy === headCell.id ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ width: '6%' }}>Airtime</TableCell>
                  <TableCell align="right" sx={{ width: '7%' }}>Data (Non-expiry)</TableCell>
                  <TableCell align="right" sx={{ width: '7%' }}>Data (Expiry)</TableCell>
                  <TableCell
                    key='balance_last_checked'
                    align='left'
                    sortDirection={orderBy === 'balance_last_checked' ? order : false}
                    sx={{ width: '11%' }}
                  >
                    <TableSortLabel
                      active={orderBy === 'balance_last_checked'}
                      direction={orderBy === 'balance_last_checked' ? order : 'asc'}
                      onClick={createSortHandler('balance_last_checked')}
                    >
                      Last Updated
                      {orderBy === 'balance_last_checked' ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sx={{ width: '7%' }}>Monthly Limit (ZAR)</TableCell>
                  <TableCell align="right" sx={{ width: '7%' }}>Month Spend (ZAR)</TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      width: '10%',
                      minWidth: 130,
                      backgroundColor: (theme) => theme.palette.action.hover,
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedSims.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={headCells.length + 5} align="center">
                      No SIMs found
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedSims.map((sim) => {
                    const isCritical = getEffectiveCriticality(sim);
                    const critSource = getCriticalitySource(sim);
                    const effectiveLimit = getEffectiveUsageLimit(sim);
                    const limitSource = getUsageLimitSource(sim);

                    // Determine effective auto top-up state
                    const isSimEnabled = sim.auto_topup_enabled;
                    const simRuleCount = sim.auto_topup_config?.length || 0;
                    const isClusterEnabled = sim.clusters?.auto_topup_enabled ?? false;
                    const clusterRuleCount = sim.clusters?.auto_topup_config?.length || 0;

                    let effectiveState: 'sim_specific' | 'cluster_inherited' | 'disabled' = 'disabled';
                    let effectiveRuleCount = 0;
                    let effectiveTooltip = 'Set Up Auto Top-Up';

                    if (isSimEnabled && simRuleCount > 0) {
                        effectiveState = 'sim_specific';
                        effectiveRuleCount = simRuleCount;
                        effectiveTooltip = 'Configure SIM Auto Top-Up';
                    } else if (!isSimEnabled && isClusterEnabled && clusterRuleCount > 0) {
                        effectiveState = 'cluster_inherited';
                        effectiveRuleCount = clusterRuleCount;
                        effectiveTooltip = 'Inheriting Cluster Rules (Click to Override)';
                    } else if (isSimEnabled && simRuleCount === 0) {
                        // Edge case: SIM enabled but no rules - treat as SIM specific config needed
                        effectiveState = 'sim_specific'; 
                        effectiveRuleCount = 0;
                        effectiveTooltip = 'Configure SIM Auto Top-Up (Enabled, 0 Rules)';
                    }
                    // If none of the above, it remains 'disabled'

                    const effectiveStatus = getEffectiveSimStatus(sim);

                    return (
                      <TableRow key={sim.id} hover>
                        <TableCell>{sim.msisdn || 'N/A'}</TableCell>
                        <TableCell>{sim.iccid || 'N/A'}</TableCell>
                        <TableCell>{sim.clusters?.name || 'Unassigned'}</TableCell>
                        <TableCell align="center">
                          <Tooltip title={critSource}>
                            <Chip
                              label={isCritical ? 'Yes' : 'No'}
                              color={isCritical ? 'error' : 'default'}
                              size="small"
                              onClick={() => handleOpenCriticalityModal(sim)}
                              sx={{ cursor: 'pointer' }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>{sim.organization.name}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip
                              label={effectiveStatus}
                              color={effectiveStatus === 'active' ? 'success' : 'default'}
                              size="small"
                            />
                            {effectiveState === 'sim_specific' && (
                               <Tooltip title={effectiveTooltip}>
                                <Chip
                                  icon={<AutorenewIcon fontSize="small" />}
                                  label={`${effectiveRuleCount} rules`}
                                  color="primary"
                                  size="small"
                                  onClick={() => handleConfigureAutoTopUp(sim)}
                                  sx={{ cursor: 'pointer' }}
                                />
                              </Tooltip>
                            )}
                            {effectiveState === 'cluster_inherited' && (
                               <Tooltip title={effectiveTooltip}>
                                <Chip
                                  icon={<AccountTreeIcon fontSize="small" />}
                                  label={`${effectiveRuleCount} rules (Cluster)`}
                                  color="info"
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleConfigureAutoTopUp(sim)}
                                  sx={{ cursor: 'pointer' }}
                                />
                              </Tooltip>
                            )}
                             {effectiveState === 'disabled' && (
                              <Tooltip title={effectiveTooltip}>
                                <Button
                                  size="small"
                                  onClick={() => handleConfigureAutoTopUp(sim)}
                                >
                                  Set Up
                                </Button>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          R{(sim.balances?.balance ?? sim.balances?.airtime ?? 0).toFixed(2)}
                        </TableCell>
                        <TableCell align="right">
                          {formatDataBalance(sim.balances?.data ?? 0)}
                        </TableCell>
                        <TableCell align="right">
                          {formatDataBalance(sim.balances?.expiry ?? 0)}
                        </TableCell>
                        <TableCell>
                          {sim.balance_last_checked ? 
                            new Date(sim.balance_last_checked).toLocaleString() :
                            'Never'
                          }
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={limitSource}>
                            <Chip
                              label={effectiveLimit === null ? 'No Limit' : `R${effectiveLimit}`}
                              color={effectiveLimit === 0 ? 'error' : (effectiveLimit ? 'primary' : 'default')}
                              size="small"
                              onClick={() => handleOpenUsageLimitModal(sim)}
                              sx={{ cursor: 'pointer' }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          {sim.current_period_topup_spend > 0 ? 
                            `R${sim.current_period_topup_spend.toFixed(2)}` : 
                            'R0.00'}
                        </TableCell>
                        <TableCell 
                          align="center"
                          sx={{ backgroundColor: (theme) => theme.palette.action.hover }}
                        >
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center' }}>
                            <Tooltip title={`Toggle Status (Currently ${effectiveStatus})`}>
                                <span>
                                    <Switch
                                        checked={effectiveStatus === 'active'}
                                        onChange={(e) => handleSimStatusToggle(sim, e.target.checked)}
                                        size="small"
                                        disabled={updatingStatusSimId === sim.id}
                                        color={effectiveStatus === 'active' ? 'success' : 'default'}
                                        sx={{ mr: -0.5 }}
                                    />
                                </span>
                             </Tooltip>
                             {sim.status_override && (
                                <Tooltip title="Status is individually overridden">
                                    <HelpOutlineIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                                </Tooltip>
                             )}
                             {updatingStatusSimId === sim.id && <CircularProgress size={16} sx={{ ml: 0.5 }}/>}
                             <Tooltip title="Refresh balance">
                               <IconButton
                                 size="small"
                                 onClick={() => handleRefreshBalance(sim.msisdn!)}
                                 disabled={!sim.msisdn || refreshingSim === sim.msisdn}
                               >
                                 {refreshingSim === sim.msisdn ? (
                                   <CircularProgress size={20} />
                                 ) : (
                                   <RefreshIcon fontSize="inherit" />
                                 )}
                               </IconButton>
                             </Tooltip>
                             <Tooltip title={effectiveTooltip}>
                               <IconButton
                                 size="small"
                                 onClick={() => handleConfigureAutoTopUp(sim)}
                               >
                                 <AutorenewIcon 
                                   fontSize="inherit" 
                                   color={effectiveState === 'sim_specific' ? 'primary' : (effectiveState === 'cluster_inherited' ? 'info' : 'inherit')} 
                                 />
                               </IconButton>
                             </Tooltip>
                             <Tooltip title="Set Criticality Override">
                               <IconButton
                                 size="small"
                                 onClick={() => handleOpenCriticalityModal(sim)}
                               >
                                 <EditIcon fontSize="inherit" />
                               </IconButton>
                             </Tooltip>
                             <Tooltip title="Reassign Cluster">
                               <IconButton
                                 size="small"
                                 onClick={() => handleOpenReassignModal(sim)}
                                 disabled={!sim.cluster_id}
                               >
                                 <SwapHorizIcon fontSize="inherit" />
                               </IconButton>
                             </Tooltip>
                             <Tooltip title="Set Usage Limit">
                               <IconButton
                                 size="small"
                                 onClick={() => handleOpenUsageLimitModal(sim)}
                               >
                                 <MonetizationOnIcon fontSize="inherit" />
                               </IconButton>
                             </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {selectedSim && (
        <AutoTopUpConfigModal
          open={configModalOpen}
          onClose={handleConfigModalClose}
          simId={selectedSim.id}
          initialConfig={selectedSim.auto_topup_config ?? []}
          initialEnabled={selectedSim.auto_topup_enabled}
        />
      )}

      {selectedSimForCrit && (
        <SetCriticalityOverrideModal
          open={criticalityModalOpen}
          onClose={handleCloseCriticalityModal}
          sim={selectedSimForCrit}
        />
      )}

      <BulkSimAssignModal
        open={bulkAssignModalOpen}
        onClose={handleCloseBulkAssignModal}
      />

      <ReassignSimModal 
        open={reassignModalOpen}
        onClose={handleCloseReassignModal}
        sim={simToReassign}
      />

      <AssignSimModal 
        open={assignSimModalOpen}
        onClose={handleCloseAssignSimModal}
      />

      {selectedSimForLimit && (
        <UsageLimitOverrideModal
          open={usageLimitModalOpen}
          onClose={handleCloseUsageLimitModal}
          sim={selectedSimForLimit}
        />
      )}

      {/* --- Deactivation Confirmation Dialog --- */}
      <Dialog
        open={confirmDeactivateOpen}
        onClose={() => setConfirmDeactivateOpen(false)}
      >
        <DialogTitle>Confirm SIM Deactivation</DialogTitle>
        <DialogContent>
          <DialogContentText component="div"> {/* Use div for richer content */} 
            <Typography gutterBottom>
                Are you sure you want to set SIM <strong>{simToDeactivate?.msisdn || simToDeactivate?.iccid}</strong> to inactive?
            </Typography>
             <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
                <strong>Important:</strong> Setting a SIM to inactive here only stops operations within this portal (like balance checks and auto top-ups).
                It does <strong>not</strong> suspend the SIM on the provider network. Usage may still incur charges if the SIM is active in a device.
            </Alert>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeactivateOpen(false)}>Cancel</Button>
          <Button 
            onClick={() => simToDeactivate && handleSimStatusToggle(simToDeactivate, true)} // Call toggle with confirmed flag
            variant="contained" 
            color="warning" // Use warning color for deactivate
            autoFocus
          >
            Confirm Deactivation
          </Button>
        </DialogActions>
      </Dialog>
      {/* --- End Deactivation Confirmation Dialog --- */}

    </Container>
  );
}

type SetCriticalityOverrideModalProps = {
  open: boolean;
  onClose: (refresh?: boolean) => void;
  sim: SimCard;
};

function SetCriticalityOverrideModal({ open, onClose, sim }: SetCriticalityOverrideModalProps) {
  const [overrideValue, setOverrideValue] = useState<boolean | null | string>(
    sim.is_critical_override
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      const valueToSave = overrideValue === 'inherit' ? null : (overrideValue as boolean | null);

      const { error: updateError } = await supabase
        .from('sim_cards')
        .update({ is_critical_override: valueToSave })
        .eq('id', sim.id);

      if (updateError) throw updateError;
      onClose(true);
    } catch (err: any) {
      console.error("Error saving criticality override:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inheritedValue = sim.clusters?.is_critical ?? false;

  return (
    <Dialog open={open} onClose={() => onClose(false)}>
      <DialogTitle>Set Criticality Override for {sim.msisdn || sim.iccid}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="body2" sx={{ mb: 2 }}>
          Inherited Criticality from Cluster '{sim.clusters?.name || 'N/A'}': <strong>{inheritedValue ? 'Yes' : 'No'}</strong>
        </Typography>
        <TextField
          select
          fullWidth
          label="Criticality Override"
          value={overrideValue === null ? 'inherit' : overrideValue}
          onChange={(e) => {
            const val = e.target.value;
            setOverrideValue(val === 'inherit' ? null : val === 'true');
          }}
          helperText="Select 'Inherit' to use the cluster's setting."
        >
          <MenuItem value={'inherit'}>Inherit ({inheritedValue ? 'Yes' : 'No'})</MenuItem>
          <MenuItem value={'true'}>Override: Yes</MenuItem>
          <MenuItem value={'false'}>Override: No</MenuItem>
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
} 